import PropTypes from "prop-types";
import { Drawer, Icon, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";

import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { makeNiceText } from "utils/formatters";

function CotizacionDrawer({ item, open, onClose, onOpenCotizacion, idProveedor, colores }) {
  const theme = useTheme();
  const isSideDrawer = useMediaQuery(theme.breakpoints.up("sm"));
  const isCotizacionAvailable = item?.licitacionEstado !== "Programada";
  const proveedorGano = item?.provisiones?.some((provision) => !provision.anulada);
  const proveedorCotizo = item?.cotizaciones?.some(
    (cotizacion) => String(cotizacion.idProveedor) === String(idProveedor)
  );
  const nombre = item?.nombre ? makeNiceText(item.nombre, "nombre") : `Pedido n° ${item?.id}`;

  const estadoProveedor = proveedorGano ? "Ganado" : proveedorCotizo ? "Licitado" : "Sin cotizar";
  const colorEstadoProveedor = proveedorGano ? "uproGreen" : proveedorCotizo ? "uproBlue" : "secondary";

  return (
    <Drawer
      anchor={isSideDrawer ? "right" : "bottom"}
      open={open}
      onClose={onClose}
      aria-labelledby="cotizacion-drawer-title"
      PaperProps={{
        sx: {
          width: isSideDrawer ? "min(88vw, 430px)" : "calc(100% - 24px)",
          maxWidth: isSideDrawer ? "100%" : "calc(100% - 24px)",
          margin: isSideDrawer ? 0 : "0 12px 12px",
          height: isSideDrawer ? "100dvh" : "auto",
          maxHeight: isSideDrawer ? "100dvh" : "84dvh",
          borderTopLeftRadius: isSideDrawer ? "24px" : "24px",
          borderTopRightRadius: isSideDrawer ? 0 : "24px",
          borderBottomLeftRadius: isSideDrawer ? "24px" : "18px",
          borderBottomRightRadius: isSideDrawer ? 0 : "18px",
          backgroundColor: "#ffffff",
          backgroundImage: "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
          boxShadow: isSideDrawer
            ? "-20px 0 52px rgba(52, 71, 103, 0.16)"
            : "0 -20px 52px rgba(52, 71, 103, 0.16)",
          overflow: "hidden",
        },
      }}
    >
      {item ? (
        <SoftBox height="100%" minHeight={0} display="flex" flexDirection="column">
          <SoftBox px={{ xs: 2, sm: 2.5 }} pt={{ xs: 1.25, sm: 2.5 }} pb={1.75}>
            {!isSideDrawer && (
              <SoftBox
                mx="auto"
                mb={1.5}
                sx={{ width: 48, height: 5, borderRadius: "999px", backgroundColor: "#d8dde6" }}
              />
            )}
            <SoftBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
              <SoftBox minWidth={0}>
                <SoftTypography variant="caption" color="primary" fontWeight="bold" display="block">
                  COTIZACIÓN #{item.id}
                </SoftTypography>
                <SoftTypography
                  id="cotizacion-drawer-title"
                  variant="h6"
                  color="dark"
                  fontWeight="bold"
                  mt={0.4}
                  sx={{ whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.3 }}
                >
                  {nombre}
                </SoftTypography>
              </SoftBox>
              <SoftButton
                variant="outlined"
                color="dark"
                size="small"
                circular
                iconOnly
                autoFocus
                onClick={onClose}
                aria-label="Cerrar detalle de la cotización"
                sx={{ minWidth: "36px !important", width: 36, height: 36, flex: "0 0 auto" }}
              >
                <Icon>close</Icon>
              </SoftButton>
            </SoftBox>
          </SoftBox>

          <SoftBox
            px={{ xs: 2, sm: 2.5 }}
            pb={2}
            flex={1}
            minHeight={0}
            sx={{ overflowY: "auto", overscrollBehavior: "contain" }}
          >
            <SoftBox display="flex" flexWrap="wrap" gap={1} mb={2.25}>
              <SoftBadge
                badgeContent={item.licitacionEstado || "Sin estado"}
                color={colores[item.licitacionEstado] || "primary"}
              />
              <SoftBadge badgeContent={estadoProveedor} color={colorEstadoProveedor} />
            </SoftBox>

            <SoftBox
              p={1.75}
              mb={1.5}
              sx={{ border: "1px solid #e9ecef", borderRadius: "14px", backgroundColor: "#ffffff" }}
            >
              <SoftTypography variant="button" color="dark" fontWeight="bold" display="block" mb={1.1}>
                Rubros
              </SoftTypography>
              <SoftBox display="flex" flexWrap="wrap" gap={0.75}>
                {item.categorias?.length ? (
                  item.categorias.map((rubro) => (
                    <SoftBox
                      key={rubro.nombre}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      px={0.9}
                      py={0.45}
                      sx={{ border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#ffffff" }}
                    >
                      <SoftBox sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "info.main" }} />
                      <SoftTypography variant="caption" color="dark" sx={{ overflowWrap: "anywhere" }}>
                        {rubro.nombre}
                      </SoftTypography>
                    </SoftBox>
                  ))
                ) : (
                  <SoftTypography variant="caption" color="text">
                    Sin rubros
                  </SoftTypography>
                )}
              </SoftBox>
            </SoftBox>

            <SoftBox
              display="grid"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              gap={1}
              p={1.75}
              sx={{ border: "1px solid #e9ecef", borderRadius: "14px", backgroundColor: "#ffffff" }}
            >
              <SoftBox>
                <SoftTypography variant="caption" color="text" display="block">
                  Inicio
                </SoftTypography>
                <SoftTypography variant="caption" color="dark" fontWeight="bold" display="block" mt={0.35}>
                  {item.licitacionInicio ? dayjs(item.licitacionInicio).format("DD/MM/YYYY HH:mm") : "-"}
                </SoftTypography>
              </SoftBox>
              <SoftBox>
                <SoftTypography variant="caption" color="text" display="block">
                  Fin
                </SoftTypography>
                <SoftTypography variant="caption" color="dark" fontWeight="bold" display="block" mt={0.35}>
                  {item.licitacionFin ? dayjs(item.licitacionFin).format("DD/MM/YYYY HH:mm") : "-"}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>

          <SoftBox
            px={{ xs: 2, sm: 2.5 }}
            py={2}
            sx={{ borderTop: "1px solid #e9ecef", backgroundColor: "rgba(255, 255, 255, 0.96)" }}
          >
            <SoftButton
              fullWidth
              color="primary"
              disabled={!isCotizacionAvailable}
              onClick={() => onOpenCotizacion(item)}
            >
              {item.puedeCotizar ? (proveedorCotizo ? "Editar cotización" : "Cotizar") : isCotizacionAvailable ? "Ver cotización" : "Disponible cuando inicie"}
              {isCotizacionAvailable && <Icon sx={{ ml: 0.75 }}>arrow_forward</Icon>}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      ) : null}
    </Drawer>
  );
}

CotizacionDrawer.propTypes = {
  item: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenCotizacion: PropTypes.func.isRequired,
  idProveedor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  colores: PropTypes.object.isRequired,
};

CotizacionDrawer.defaultProps = {
  item: null,
  idProveedor: null,
};

export default CotizacionDrawer;
